import React, { useEffect, useState } from "react";
import "moment-timezone";
import { faMinus, faPlus, faRefresh } from "@fortawesome/pro-regular-svg-icons";

import { StatusCode } from "api/protocols";
import { IProduct } from "redux/reducers/models/product";

import Sheet from "components/sheet/Sheet";
import Input from "components/form/input/Input";
import { ButtonNew as Button } from "components/buttonNew/index";
import "pages/secure/facility/teesheet/styles.scss";
import { showError, showSuccess } from "redux/actions/ui";
// import { GetVariant } from "api/rpc/2024-04/facilityAdmin/product/variant";
import { GetProduct } from "api/rpc/2024-04/facilityAdmin/product/product";
import { IProductState } from "elements/register/Products";
import Icon from "components/icon/Icon";
import { PRODUCT_FOLDERS_KEY } from "pages/secure/facility/settings/folders/Folders";
import { useTranslation } from "react-i18next";
import "elements/register/register.scss";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { FolderMenu } from "components/folderMenu/FolderMenu";
import ProductsNew from "../../register/registerNew/ProductsNew";
import { GetFolder, IFolder } from "api/rpc/2024-04/facilityAdmin/product/folder";
import RegisterContext from "../../register/registerNew/RegisterContext";
import Portal from "elements/Portal";
import { LocaleCurrency } from "helpers/Locale";
import { calculateHexColorBrightness } from "helpers/Helpers";
import Divider from "components/divider";
import classNames from "classnames";
import "./exchangeItems.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface IProps {
    open: boolean;
    onCancel: () => void;
    onOk: (items: Array<IProduct>) => void;
    itemReplacementTitle: string;
    presetItems?: Array<IProduct>;
}

export interface IMainState {
    products: IProduct[];
    folders: IFolder[];
    selectedFolder: IFolder;
    productState: IProductState;
    selectedVariants: Array<IProduct & {quantity?: number}>;
}

export interface IFilterState {
    search: string;
    sort: string;
    barcodeSearch: boolean;
}

export default function ExchangeItemsModal(props: IProps) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { open, onOk, onCancel, itemReplacementTitle, presetItems } = props;
    const { facilityStore } = useAppSelector(store => store);

    const [state, setState] = useState<IMainState>({
        products: [],
        folders: [],
        selectedFolder: null,
        selectedVariants: [],
        productState: {
            selectedProduct: undefined,
            variants: [],
            variantSheetOpen: false,
        },
    });

    const [filterState, setFilterState] = useState<IFilterState>({
        search: "",
        sort: "alpha",
        barcodeSearch: false,
    });

    function updateMainState(newMainState: Partial<IMainState>) {
        setState((cur: IMainState) => {
            return { ...cur, ...newMainState };
        });
    }

    function updateFilterState(newFilterState: Partial<IFilterState>) {
        setFilterState(cur => {
            return { ...cur, ...newFilterState };
        });
    }

    useEffect(() => {
        if (open && (state?.folders === undefined || state.folders.length === 0)) {
            void loadFolders();
        }
        if (presetItems && presetItems?.length > 0) {
            updateMainState({selectedVariants: [...presetItems]});
        }
    }, [open]);

    useEffect(() => {
        let mounted = true;
        let timeoutId: NodeJS.Timeout = null;
        const search = () => {
            timeoutId = global.setTimeout(() => {
                void (async () => {
                    try {
                        if (filterState.search === "") {
                            if (mounted) {
                                updateMainState({ products: [] });
                            }
                            return;
                        } else {
                            const res = await loadProducts();
                            if (mounted) {
                                updateMainState({ products: res });
                            }
                        }
                    } catch (error) {
                        console.log("err", error);
                    }
                    return;
                })();
            }, 500);
        };
        search();
        return () => {
            mounted = false;
            clearTimeout(timeoutId);
        };
    }, [filterState.search]);

    async function loadFolders() {
        const register = JSON.parse(localStorage.getItem("register"));

        if (!register) {
            dispatch(showError(t("Invalid register")));
            return;
        }

        const folderRes = await GetFolder({ register_group_id: register.register_group_id, extended: false }, true);

        if (folderRes.status !== StatusCode.OK) {
            dispatch(showError(t("Failed to load folders")));
            return;
        }

        const folders = folderRes.data;

        const defaultFolderId = Number(localStorage.getItem("default_folder_id"));
        let selectedFolder: IFolder = undefined;

        if (defaultFolderId > 0) {
            selectedFolder = folders.find((folder: IFolder) => folder.id === defaultFolderId);
        } else {
            selectedFolder = folders.find((folder: IFolder) => folder.default === true);
        }

        if (!selectedFolder) {
            selectedFolder = folders?.[0];
        }

        updateMainState({ folders: folders });

        if (selectedFolder) {
            void handleChangeSelectedFolder(selectedFolder);
        }
    }

    const loadProducts = async () => {
        if (state?.products !== undefined) {
            setState(prev => ({ ...prev, products: undefined }));
        }
        const productRes = await GetProduct({ extended: true, search: filterState.search }, false);
        if (productRes.status !== StatusCode.OK) {
            setState(prev => ({ ...prev, products: [] }));
            dispatch(showError(t("Error getting products")));
            return [];
        }
        return productRes.data;
    };

    function renderProducts() {
        if (filterState.search && state.products) {
            const products = state.products?.sort((firstProd: IProduct, secondProd: IProduct) => {
                if (filterState.sort === "alpha") {
                    if (firstProd.title < secondProd.title) {
                        return -1;
                    } else if (firstProd.title > secondProd.title) {
                        return 1;
                    } else {
                        return 0;
                    }
                }
            }) as [];
            return { useGridPositions: false, products };
        } else {
            const products = state.selectedFolder?.products ?? ([] as []);
            return { useGridPositions: true, products };
        }
    }

    async function handleChangeSelectedFolder(selectedFolder: IFolder) {
        if (selectedFolder?.id === undefined) {
            return;
        }

        const storedProductFolders: IFolder[] = JSON.parse(localStorage.getItem(PRODUCT_FOLDERS_KEY)) ?? [];
        const storedProductFolder = storedProductFolders.find(productFolder => productFolder?.id === selectedFolder.id);

        const selectedFolderDate = new Date(selectedFolder.updated_at);
        const storedProductFolderDate = new Date(storedProductFolder?.updated_at);

        const storedProductFolderIsValid =
            !isNaN(selectedFolderDate?.getTime()) &&
            !isNaN(storedProductFolderDate?.getTime()) &&
            storedProductFolderDate.getTime() >= selectedFolderDate.getTime();

        if (storedProductFolderIsValid) {
            updateMainState({ selectedFolder: storedProductFolder });
        } else {
            const folderRes = await GetFolder({ id: selectedFolder.id, extended: true }, true);

            if (folderRes.status !== StatusCode.OK || folderRes.data?.length === undefined || folderRes.data.length === 0) {
                dispatch(showError(t("Failed to load folder products")));
                return;
            }

            const newSelectedFolder = folderRes.data[0];

            if (!storedProductFolder) {
                const updatedStoredProductFolders = [...storedProductFolders, newSelectedFolder];
                localStorage.setItem(PRODUCT_FOLDERS_KEY, JSON.stringify(updatedStoredProductFolders));
            } else {
                const updatedStoredProductFolders = storedProductFolders.map(productFolder => {
                    if (productFolder.id === newSelectedFolder.id) {
                        return newSelectedFolder;
                    } else {
                        return productFolder;
                    }
                });

                localStorage.setItem(PRODUCT_FOLDERS_KEY, JSON.stringify(updatedStoredProductFolders));
            }

            updateMainState({ selectedFolder: newSelectedFolder });
        }
    }

    function refreshFolderStorage() {
        localStorage.removeItem(PRODUCT_FOLDERS_KEY);

        if (state.selectedFolder) {
            void handleChangeSelectedFolder(state.selectedFolder);
        }

        dispatch(showSuccess(t("Successfully refreshed folder storage")));
    }

    function variantAdd(product: IProduct) {
        const foundVariantIndex = state?.selectedVariants?.findIndex(searchedProduct => searchedProduct?.variants[0]?.id === product?.variants[0]?.id);
        if (foundVariantIndex !== -1) {
            // Update quantity on existing selected variant
            const updatedVariants = [...state.selectedVariants];
            const prevQuantity = updatedVariants[foundVariantIndex]?.variants[0]?.quantity ?? 0;
            updatedVariants[foundVariantIndex] = {...updatedVariants[foundVariantIndex], variants: [{...updatedVariants[foundVariantIndex]?.variants[0], quantity: prevQuantity + 1}]};
            updateMainState({ selectedVariants: updatedVariants });
        } else {
            // Add new variant
            const updatedProduct = {...product, variants: [{...product?.variants[0], quantity: 1}]};
            updateMainState({ selectedVariants: [...state.selectedVariants, updatedProduct] });
        }
    }

    function adjustQuantity(type: "increment" | "decrement", index: number) {
        let updatedVariants = state?.selectedVariants ? [...state.selectedVariants] : [];
        const currentVariant = updatedVariants[index]?.variants[0];
        const updatedVariant = {...updatedVariants[index]?.variants[0], quantity: type === "increment" ? currentVariant?.quantity + 1 : currentVariant?.quantity - 1};
        const updatedProduct = {...updatedVariants[index], variants: [updatedVariant]};
        updatedVariants[index] = updatedProduct;
        if (updatedVariant?.quantity <= 0) {
            updatedVariants = updatedVariants?.filter((_, prodIndex) => prodIndex !== index);
        }
        updateMainState({ selectedVariants: updatedVariants });
    }

    function onClose() {
        updateMainState({ selectedVariants: [] });
        void onCancel();
    }

    function handleOk() {
        void onOk([...state.selectedVariants]);
        void onClose();
    }

    return (
        <Portal isMounted={open}>
            <Sheet
                title={t("Add replacement(s)")}
                open={open}
                size="large"
                closable
                onCancel={onClose}
                onOk={handleOk}
                okText={t("Add products")}
                supportText={t("Search or tap any product to add replacement for {{itemReplacementTitle}}", { itemReplacementTitle: itemReplacementTitle })}
            >
                <RegisterContext>
                    <div className="ui-register">
                        <div className="ui-register-folders_container">
                            <FolderMenu
                                options={state?.folders}
                                optionTitle="title"
                                defaultSelected={state?.folders?.findIndex(
                                    folder => folder?.id === state?.selectedFolder?.id,
                                )}
                                onClick={option => {
                                    void handleChangeSelectedFolder(option as IFolder);
                                }}
                            />
                        </div>
                        <div className="ui-register-new-products-container">
                            <div className="ui-register-new-products-content">
                                <div className="ui-register-top-bar">
                                    <div className="flex justify-between mb-4">
                                        <div className="flex-grow">
                                            <Input
                                                id="productSearchId"
                                                value={filterState.search}
                                                onChange={(value: any) => updateFilterState({ search: value.target.value })}
                                                type="search"
                                                placeholder={t("Search for a product...")}
                                                labelHidden
                                                autoFocus
                                                trailingButtons={[
                                                    <Button key={1} type="secondary" onClick={refreshFolderStorage}>
                                                        <Icon icon={faRefresh} size="medium" />
                                                    </Button>,
                                                ]}
                                            />
                                        </div>
                                    </div>
                                </div>
                                {state?.selectedVariants?.length > 0 ? (
                                    <>
                                    <p className="ui-exchange-items-replacement-text">{t("Selected products for replacement")}:</p>
                                        <div className="ui-register-products ui-register-products--folder">
                                            {state?.selectedVariants?.map((product, index) => {
                                                const backgroundColorBrightness = product.meta?.button_color ? calculateHexColorBrightness(product.meta.button_color) : null;
                                                const foregroundColor =
                                                    backgroundColorBrightness === null ? "white" : backgroundColorBrightness < 50 ? "white" : "black";

                                                const textBgStyle = (product?.default_image?.source && facilityStore?.teeSheetSettings?.show_product_images)
                                                    ? { backgroundColor: "rgba(0, 0, 0, 0.8)", borderRadius: "4px", padding: "2px 4px" }
                                                    : {};

                                                const variantTitle = product?.variants[0]?.title;

                                                return (
                                                    <div className="product_container ui-exchange-items-product-container" key={index}>
                                                        <div
                                                            className={"product_content "}
                                                            style={
                                                                (product?.default_image?.source && facilityStore?.teeSheetSettings?.show_product_images)
                                                                    ? {
                                                                        backgroundImage: `url(${product.default_image.source})`,
                                                                        backgroundSize: "100% auto",
                                                                        backgroundRepeat: "no-repeat",
                                                                        backgroundPosition: "center",
                                                                    }
                                                                    : product.meta?.button_color
                                                                        ? { background: product?.meta?.button_color, color: foregroundColor }
                                                                        : {}
                                                            }
                                                        >
                                                            <div style={textBgStyle}>
                                                                <div className="product-title" style={{userSelect: "none"}}>{product.preferred_title ? product.preferred_title : product.title}</div>
                                                                {product.subtitle ? <div className="product-vendor" style={{userSelect: "none"}}>{product.subtitle}</div> : null}
                                                                {variantTitle !== product?.title && variantTitle !== product?.preferred_title ? <div className="product-vendor" style={{userSelect: "none"}}>{variantTitle}</div> : null}
                                                            </div>
                                                            <div className="product-details" style={product?.variant_count === 1 && product?.variants[0]?.inventory_item?.tracked && facilityStore?.teeSheetSettings?.product_card_display_inventory ? textBgStyle : undefined}>
                              
                                                                <div className="product-price" style={!(product?.variant_count === 1 && product?.variants[0]?.inventory_item?.tracked && facilityStore?.teeSheetSettings?.product_card_display_inventory) && product.variants?.length === 1 ? textBgStyle : undefined}>
                                                                    {product.variants?.length === 1 ? (
                                                                        <LocaleCurrency style={{userSelect: "none"}} currency={facilityStore?.facility?.default_currency_code} amount={product?.variants[0]?.price?.toFixed(2)} />
                                                                    ) : (
                                                                        ""
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="ui-exchange-items-product-container-inner">
                                                            <FontAwesomeIcon style={{color: foregroundColor, cursor: "pointer"}} icon={faMinus} onClick={() => adjustQuantity("decrement", index)}/>
                                                            <p style={{color: foregroundColor, fontSize: "20px", fontWeight: "500", userSelect: "none"}}>{product?.variants[0]?.quantity}</p>
                                                            <FontAwesomeIcon style={{color: foregroundColor, cursor: "pointer"}} icon={faPlus} onClick={() => adjustQuantity("increment", index)}/>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <Divider />
                                    </>
                                ) : null}
                                <div className={classNames("product-content", { "ui-exchange-items-product-container": state?.selectedVariants?.length > 0 })}>
                                    <ProductsNew
                                        {...renderProducts()}
                                        cart={undefined}
                                        productsLoadingMessage={state?.products === undefined ? t("Loading products...") : undefined}
                                        selectedFolder={state?.selectedFolder}
                                        onVariantClick={product => variantAdd(product)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </RegisterContext>
            </Sheet>
        </Portal>
    );
}
